import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { CLIMATE_QUESTIONS } from '@/config/climateQuestions';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

const LIKERT_LABELS = [
  'Totalmente en desacuerdo',
  'En desacuerdo',
  'Neutral',
  'De acuerdo',
  'Totalmente de acuerdo',
] as const;

const LIKERT_COLORS = [
  'bg-red-500/90 hover:bg-red-500 text-white',
  'bg-orange-500/90 hover:bg-orange-500 text-white',
  'bg-amber-500/90 hover:bg-amber-500 text-white',
  'bg-lime-500/90 hover:bg-lime-600 text-white',
  'bg-green-500/90 hover:bg-green-600 text-white',
] as const;

const ENPS_LABELS: Record<number, string> = {
  0: 'No la recomendaría',
  10: 'La recomendaría totalmente',
};

export function ClimateAssessmentPage() {
  const { id } = useParams<{ id: string }>();
  const [status, setStatus] = useState<'loading' | 'invalid' | 'ready' | 'completed'>('loading');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number | string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    if (!id) {
      setStatus('ready');
      return;
    }
    getDoc(doc(db, 'climate_assessments', id))
      .then((snap) => {
        if (!snap.exists()) {
          setStatus('invalid');
          return;
        }
        const data = snap.data();
        if (data?.status === 'completed') {
          setCompleted(true);
        } else {
          const saved = data?.answers as Record<string, number | string> | undefined;
          if (saved && Object.keys(saved).length > 0) {
            setAnswers(saved);
          }
        }
        setStatus('ready');
      })
      .catch(() => setStatus('invalid'));
  }, [id]);

  const question = CLIMATE_QUESTIONS[currentIndex];
  const totalQuestions = CLIMATE_QUESTIONS.length;
  const progress = ((currentIndex + 1) / totalQuestions) * 100;
  const isLastQuestion = currentIndex === totalQuestions - 1;
  const isOpenOptional = question?.type === 'open' && question?.id === 'open_comments';

  const currentAnswer = question ? answers[question.id] : undefined;

  const canProceed = (): boolean => {
    if (!question) return false;
    if (isOpenOptional) return true;
    if (question.type === 'likert' || question.type === 'enps') {
      return typeof currentAnswer === 'number';
    }
    if (question.type === 'open') return true;
    return false;
  };

  const handleAnswer = (value: number | string) => {
    if (!question) return;
    setAnswers((prev) => ({ ...prev, [question.id]: value }));
  };

  const handleNext = async () => {
    if (isLastQuestion) {
      setIsSubmitting(true);
      try {
        if (id) {
          await updateDoc(doc(db, 'climate_assessments', id), {
            answers,
            status: 'completed',
            completedAt: new Date().toISOString(),
          });
        } else {
          console.log('[Climate] Respuestas (sin id):', answers);
        }
        setCompleted(true);
      } catch (err) {
        console.error('[Climate] Error al guardar:', err);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setCurrentIndex((i) => Math.min(i + 1, totalQuestions - 1));
    }
  };

  const handlePrev = () => {
    setCurrentIndex((i) => Math.max(i - 1, 0));
  };

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
        <p className="text-muted-foreground">Cargando encuesta...</p>
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 px-4 dark:from-slate-950 dark:to-slate-900">
        <div className="text-center">
          <p className="font-medium text-destructive">Enlace inválido o expirado</p>
          <p className="mt-2 text-sm text-muted-foreground">
            El enlace de la encuesta no es válido. Contacta a tu área de Recursos Humanos.
          </p>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 px-4 py-12 dark:from-slate-950 dark:to-slate-900">
        <div className="mx-auto max-w-md rounded-2xl border border-green-200 bg-white p-8 text-center shadow-lg dark:border-green-800 dark:bg-slate-900">
          <div className="mb-4 text-5xl">✓</div>
          <h2 className="text-xl font-semibold text-green-700 dark:text-green-400">
            ¡Gracias por tu participación!
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Tus respuestas han sido registradas de forma anónima. Tu opinión nos ayuda a mejorar.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-slate-200/80 bg-white/95 backdrop-blur-sm dark:border-slate-800 dark:bg-slate-950/95">
        <div className="mx-auto flex max-w-2xl flex-col gap-3 px-4 py-4">
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold text-slate-800 dark:text-slate-100">
              PsyTalent
            </span>
            <span className="text-sm text-muted-foreground">
              {currentIndex + 1} / {totalQuestions}
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
            <div
              className="h-full rounded-full bg-primary transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex flex-1 flex-col px-4 py-8 sm:py-12">
        <div className="mx-auto w-full max-w-2xl">
          {question && (
            <div className="transition-opacity duration-300">
              <h2 className="text-xl font-medium leading-relaxed text-slate-800 dark:text-slate-100 sm:text-2xl">
                {question.text}
              </h2>

              <div className="mt-8">
                {question.type === 'likert' && (
                  <LikertInput
                    value={currentAnswer as number | undefined}
                    onChange={handleAnswer}
                  />
                )}
                {question.type === 'enps' && (
                  <EnpsInput
                    value={currentAnswer as number | undefined}
                    onChange={handleAnswer}
                  />
                )}
                {question.type === 'open' && (
                  <OpenInput
                    value={currentAnswer as string | undefined}
                    onChange={handleAnswer}
                    placeholder={question.placeholder}
                    optional={isOpenOptional}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Navigation */}
      <footer className="sticky bottom-0 border-t border-slate-200/80 bg-white/95 backdrop-blur-sm dark:border-slate-800 dark:bg-slate-950/95">
        <div className="mx-auto flex max-w-2xl gap-4 px-4 py-4">
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="min-h-[52px] flex-1 touch-manipulation text-base"
          >
            Anterior
          </Button>
          <Button
            type="button"
            size="lg"
            onClick={handleNext}
            disabled={!canProceed()}
            className="min-h-[52px] flex-1 touch-manipulation text-base"
          >
            {isSubmitting ? 'Enviando...' : isLastQuestion ? 'Enviar' : 'Siguiente'}
          </Button>
        </div>
      </footer>
    </div>
  );
}

function LikertInput({
  value,
  onChange,
}: {
  value: number | undefined;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-5 gap-2 sm:gap-3">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className={cn(
              'flex min-h-[56px] flex-col items-center justify-center rounded-xl border-2 px-2 py-3 transition-all touch-manipulation',
              value === n
                ? LIKERT_COLORS[n - 1]
                : 'border-slate-200 bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-600'
            )}
          >
            <span className="text-lg font-bold sm:text-xl">{n}</span>
          </button>
        ))}
      </div>
      <div className="flex justify-between gap-1 text-xs text-muted-foreground">
        <span className="max-w-[30%]">{LIKERT_LABELS[0]}</span>
        <span className="max-w-[30%] text-right">{LIKERT_LABELS[4]}</span>
      </div>
    </div>
  );
}

function EnpsInput({
  value,
  onChange,
}: {
  value: number | undefined;
  onChange: (v: number) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-center gap-2">
        {Array.from({ length: 11 }, (_, i) => i).map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className={cn(
              'flex h-12 w-12 min-w-[44px] items-center justify-center rounded-lg border-2 text-lg font-semibold transition-all touch-manipulation sm:h-14 sm:w-14 sm:min-w-[52px] sm:text-xl',
              value === n
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-slate-200 bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-600'
            )}
          >
            {n}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{ENPS_LABELS[0]}</span>
        <span>{ENPS_LABELS[10]}</span>
      </div>
    </div>
  );
}

function OpenInput({
  value,
  onChange,
  placeholder,
  optional,
}: {
  value: string | undefined;
  onChange: (v: string) => void;
  placeholder?: string;
  optional?: boolean;
}) {
  return (
    <div className="space-y-2">
      <textarea
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={5}
        className="w-full resize-y rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-base placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-slate-700 dark:bg-slate-900"
      />
      {optional && (
        <p className="text-xs text-muted-foreground">Este campo es opcional.</p>
      )}
    </div>
  );
}
